/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { apiClient } from '@/lib/apiClient';
import type { Dtc, Program, VinDetails, VinHistory, VinSaveRequest } from './models';

/** Programs (year/make/model) available for vehicle search. Role-filtered server-side. */
export async function getVehicleSearchPrograms(): Promise<Program[]> {
  const { data } = await apiClient.get<Program[]>(
    '/ediagnostics/v1/vehicle/information/programs/vehicle-search',
  );
  return data ?? [];
}

/** Currently-available programs for program/build-release and DTC search. */
export async function getProgramSearchPrograms(): Promise<Program[]> {
  const { data } = await apiClient.get<Program[]>(
    '/ediagnostics/v1/vehicle/information/programs/program-search',
  );
  return data ?? [];
}

/** Recently opened VINs for the given user. */
export async function getVinHistory(userId: string): Promise<VinHistory[]> {
  const { data } = await apiClient.get<VinHistory[]>('/ediagnostics/v1/vehicles/vin/history', {
    params: { userId },
  });
  return data ?? [];
}

/** Records that a VIN was opened so it appears in the user's recent history. */
export async function saveVinHistory(request: VinSaveRequest): Promise<void> {
  await apiClient.post('/ediagnostics/v1/vehicles/vin/history', request);
}

/** Resolves VIN identity/DTCs via AVP/AVI; omit system for identity-only. Returns null when unknown. */
export async function getVinDetails(
  vin: string,
  system?: string,
  dtcs?: string[],
): Promise<VinDetails | null> {
  const { data } = await apiClient.post<VinDetails[]>(
    `/ediagnostics/v1/vehicles/dealers/service/${encodeURIComponent(vin)}`,
    dtcs ?? [],
    { params: system ? { system } : undefined },
  );
  const details = data?.[0] ?? null;
  // AVP reports a fault once per module that raised it, so a code seen by two modules arrives
  // twice. Everything downstream reads the list as a set -- row keys, fault counts, ticked
  // selections -- so collapse the repeats here rather than in each consumer. An absent list stays
  // absent: callers read that as "never answered", which is not the same as a clear vehicle.
  return details?.openDtcCodes
    ? { ...details, openDtcCodes: [...new Set(details.openDtcCodes)] }
    : details;
}

/** Available DTCs for a platform/program (first page of the paginated catalogue). */
export async function getPlatformDtcs(programId: number | string): Promise<Dtc[]> {
  const { data } = await apiClient.get<{
    items?: Array<{ code: string; gmName?: string; name?: string }>;
  }>(`/api/v1/platforms/${programId}/dtcs`, { params: { page: 0, size: 25 } });
  return (data?.items ?? []).map((d) => ({ code: d.code, name: d.name ?? d.gmName ?? '' }));
}
